import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Borrower,
  ENVIRONMENT,
  getEncryptionValueForLoan,
  HelocDrawEligibilityEnum,
  isHelocLoan,
  Loan,
  LoanKeyEnum,
  PaymentLoanStatusValEnum,
  UserProfile,
} from '@ld.consumer-portal/models';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';

export enum ServisBotIntent {
  DASHBOARD = 'DASHBOARD',
  PAYMENT = 'PAYMENT',
  AUTOPAY = 'AUTOPAY',
  PAYOFF_QUOTE = 'PAYOFF_QUOTE',
  SECURE_MESSAGE_CENTER = 'SECURE_MESSAGE_CENTER',
  PAYMENT_ASSISTANCE = 'PAYMENT_ASSISTANCE',
  INSURANCE_CLAIMS = 'INSURANCE_CLAIMS',
  HOMEOWNERS_INSURANCE = 'HOMEOWNERS_INSURANCE',
  PROPERTY_TAXES = 'PROPERTY_TAXES',
  FAQS = 'FAQS',
  STATEMENTS_AND_DOCUMENTS = 'STATEMENTS_AND_DOCUMENTS',
  USER_PROFILE = 'USER_PROFILE',
  REQUEST_FUNDS = 'REQUEST_FUNDS',
  MANAGE_PAPERLESS = 'MANAGE_PAPERLESS',
  HELOC_DASHBOARD = 'HELOC_DASHBOARD',
  HELOC_DRAW = 'HELOC_DRAW',
  HELOC_PAYMENT = 'HELOC_PAYMENT',
  HELOC_AUTOPAY = 'HELOC_AUTOPAY',
}

interface ServisBotLoanDetails {
  loanNumber: string;
  encryptionValue?: string;
  isOTP: boolean;
  isAutopay: boolean;
  isHeloc: boolean;
  isBotEligible: boolean;
}

export interface HelocLoanInfoContext {
  loanNumber: string;
  annualInterestRate?: number;
  ytdInterestPaid?: number;
  priorYearInterestPaid?: number;
  availableHELOCAmount?: number;
  helocLimitAmount?: number;
  noteDate?: string;
  nextPaymentDueDate?: string;
  currentPrincipalBalance?: number;
  currentInterestAmount?: number;
  remainingTerm?: number;
  originalTerm?: number;
  loanMaturityDate?: string;
  paymentDue?: number;
  totalFeeAmount?: number;
  currentBillPrincipalInterestAmount?: number;
  isBillGenerated?: boolean;
  lastPaymentAmount?: number;
  lastPaymentDate?: string;
  showReturnAfter16thMessage?: boolean;
  totalPaymentDue?: number;
  totalPrincipalInterestAmount?: number;
  drawEligibilityStatus?: HelocDrawEligibilityEnum;
}

@Injectable({
  providedIn: 'root',
})
export class ServisBotService {
  private readonly router = inject(Router);
  private readonly env = inject(ENVIRONMENT);
  private readonly logger = inject(LoggingService);
  private readonly platformService = inject(PlatformService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private servisBotApi: any = null;

  private selectedLoan: Loan | null = null;

  private identity: UserProfile | null = null;

  private previousLoanCount = 0;

  private isInitializing = false;

  private helocLoanInfo: HelocLoanInfoContext | null = null;

  private lastOpenRequestTime = 0;

  private static readonly OPEN_GRACE_PERIOD_MS = 1500;

  readonly isInitialized = signal(false);

  readonly isVisible = signal(false);

  readonly isLoading = signal(false);

  private get config() {
    return this.env.appSettings?.servisBot;
  }

  get isEnabled(): boolean {
    return this.env.appSettings?.AppSettings?.enableServisBot === 'true' && !!this.config;
  }

  async handle(
    selectedLoan: Loan,
    loans: Loan[],
    userProfile: UserProfile,
    helocInfo?: HelocLoanInfoContext,
  ): Promise<void> {
    if (!this.isEnabled || !this.config) {
      this.logger.logWarning('ServisBot is not enabled or configured');
      return;
    }

    this.identity = userProfile;

    if (helocInfo) {
      this.helocLoanInfo = helocInfo;
    } else if (!isHelocLoan(selectedLoan.loanNumber, this.identity)) {
      this.helocLoanInfo = null;
    }

    if (!this.selectedLoan) {
      if (this.isInitializing) {
        return;
      }

      const loanList = this.buildLoanList(loans);
      await this.initializeBot(selectedLoan, loanList, userProfile);
      return;
    }

    if (selectedLoan.loanNumber !== this.selectedLoan.loanNumber) {
      if (loans.length !== this.previousLoanCount) {
        const loanList = this.buildLoanList(loans);
        await this.reinitializeBot(loanList, selectedLoan);
      } else {
        await this.updateBot(selectedLoan);
      }
    }
  }

  private async initializeBot(loan: Loan, loanList: ServisBotLoanDetails[], userProfile: UserProfile): Promise<void> {
    if (this.selectedLoan) {
      await this.updateBot(loan);
      return;
    }

    this.isInitializing = true;

    try {

      const loanBorrower = this.getLoanBorrowerInfo(loan, userProfile);

      const customerDetails = {
        loanNumberArray: loanList, // All loans tied to this user with encryptionValue, isOTP, isAutopay, etc.
        email: loanBorrower?.email ?? userProfile.email,
        userId: loanBorrower?.id ?? userProfile.globalUserID,
        firstName: loanBorrower?.firstName ?? userProfile.firstName,
        lastName: loanBorrower?.lastName ?? userProfile.lastName,
      };

      if (this.servisBotApi) {
        await this.clear();
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!(window as any).ServisBot) {
        this.logger.logWarning('ServisBot SDK not loaded');
        return;
      }

      const config = this.config;
      if (!config) {
        return;
      }

      // Use mobile endpoint if on native mobile and mobileEndpoint is configured
      const isMobile = this.platformService.isNativeMobile();
      const endpoint = isMobile && config.mobileEndpoint ? config.mobileEndpoint : config.endpoint;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.servisBotApi = await (window as any).ServisBot.init({
        organization: config.env,
        endpoint: endpoint,
        sbRegion: config.envRegion,
        displayWidget: 'true',
        version: '2',
        customStyle: config.customStyle,
        customerReference: loan.loanNumber,
        context: {
          customerDetails,
          customerReference: loan.loanNumber,
          userId: loanBorrower?.email ?? userProfile.email,
          helocLoanInfo: this.helocLoanInfo, // null for non-HELOC, populated for HELOC
        },
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).resetServisBotApi = this.resetHandler.bind(this);

      this.selectedLoan = loan;
      this.previousLoanCount = loanList.length;

      await this.waitForSdkReady();

      this.isInitialized.set(true);

      this.setupNotificationHandler();

      this.setupVisibilityHandler();

    } catch (error) {
      this.logger.logException(error, 'Failed to initialize ServisBot');
      this.selectedLoan = null;
      this.isInitialized.set(false);
    } finally {
      this.isInitializing = false;
    }
  }

  private async reinitializeBot(loanList: ServisBotLoanDetails[], loan: Loan): Promise<void> {
    if (!this.selectedLoan || !this.servisBotApi) {
      return;
    }

    try {

      const pageEvent = {
        alias: 'loanListUpdated',
        body: {
          loanNumberArray: loanList,
          loanNumber: loan.loanNumber,
          helocLoanInfo: this.helocLoanInfo,
        },
      };

      this.selectedLoan = loan;
      this.previousLoanCount = loanList.length;
      await this.servisBotApi.pageEvent(pageEvent);

    } catch (error) {
      this.logger.logException(error, 'Failed to reinitialize ServisBot');
    }
  }

  private async updateBot(loan: Loan): Promise<void> {
    if (!this.selectedLoan || !this.servisBotApi) {
      return;
    }

    try {
      const pageEvent = {
        alias: 'loanSelected',
        body: {
          loanNumber: loan.loanNumber,
          helocLoanInfo: this.helocLoanInfo,
        },
      };

      this.selectedLoan = loan;
      await this.servisBotApi.pageEvent(pageEvent);

    } catch (error) {
      this.logger.logException(error, 'Failed to update ServisBot');
    }
  }

  private toggle(open?: boolean): void {
    if (!this.isEnabled || !this.config) {
      this.logger.logWarning('ServisBot toggle called but service is not enabled or configured');
      return;
    }

    if (!this.isInitialized()) {
      this.logger.logWarning('ServisBot toggle called but service is not initialized');
      this.isLoading.set(false);
      return;
    }

    if (!this.servisBotApi) {
      this.logger.logWarning('ServisBot toggle called but servisBotApi is null');
      this.isLoading.set(false);
      return;
    }

    const shouldOpen = open ?? !this.isVisible();
    this.isVisible.set(shouldOpen);

    if (shouldOpen) {
      this.lastOpenRequestTime = Date.now();
    }

    if (shouldOpen) {
      document.body.classList.add('servisbot-chat-open');
    } else {
      document.body.classList.remove('servisbot-chat-open');
      document.body.classList.remove('sb-messenger-open');
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    if (shouldOpen) {
      this.isLoading.set(true);
      setTimeout(() => {
        this.isLoading.set(false);
        if (this.isVisible()) {
          const iframe = document.getElementById('servisbot-messenger-iframe');
          if (!iframe) {
            this.logger.logWarning('ServisBot iframe not found after opening');
          }
        }
      }, 3000);
    } else {
      this.isLoading.set(false);
    }

    const postMessageSent = this.postMessageToMessenger(shouldOpen ? 'open' : 'close');
    this.logger.logTrace(`ServisBot postMessage ${shouldOpen ? 'open' : 'close'}: ${postMessageSent ? 'sent' : 'failed'}`);

    try {
      if (shouldOpen) {
        if (typeof this.servisBotApi.open === 'function') {
          this.logger.logTrace('ServisBot calling open() method');
          this.servisBotApi.open();
        } else if (typeof this.servisBotApi.show === 'function') {
          this.logger.logTrace('ServisBot calling show() method');
          this.servisBotApi.show();
        } else if (this.platformService.isIOS() && typeof this.servisBotApi.toggle === 'function') {
          this.logger.logTrace('ServisBot calling toggle() to open (iOS)');
          this.servisBotApi.toggle();
        } else if (typeof this.servisBotApi.pageEvent === 'function') {
          this.logger.logTrace('ServisBot calling pageEvent to open');
          this.servisBotApi
            .pageEvent({
              alias: 'toggleServisBotWindow',
              body: { loanNumber: this.selectedLoan?.loanNumber, openChatBotWindow: true },
            })
            .catch((error: unknown) => {
              this.isLoading.set(false);
              this.isVisible.set(false);
              document.body.classList.remove('servisbot-chat-open');
              this.logger.logException(error, 'Failed to open ServisBot via pageEvent');
            });
        } else {
          this.logger.logWarning('ServisBot API has no suitable open method');
          this.isVisible.set(false);
          this.isLoading.set(false);
          document.body.classList.remove('servisbot-chat-open');
        }
      } else {
        if (typeof this.servisBotApi.close === 'function') {
          this.logger.logTrace('ServisBot calling close() method');
          this.servisBotApi.close();
        } else if (typeof this.servisBotApi.hide === 'function') {
          this.logger.logTrace('ServisBot calling hide() method');
          this.servisBotApi.hide();
        } else if (this.platformService.isIOS() && typeof this.servisBotApi.toggle === 'function') {
          this.logger.logTrace('ServisBot calling toggle() to close (iOS)');
          this.servisBotApi.toggle();
        } else if (typeof this.servisBotApi.pageEvent === 'function') {
          this.logger.logTrace('ServisBot calling pageEvent to close');
          this.servisBotApi
            .pageEvent({
              alias: 'toggleServisBotWindow',
              body: { loanNumber: this.selectedLoan?.loanNumber, openChatBotWindow: false },
            })
            .catch((error: unknown) => {
              this.isLoading.set(false);
              this.isVisible.set(false);
              document.body.classList.remove('servisbot-chat-open');
              this.logger.logException(error, 'Failed to close ServisBot via pageEvent');
            });
        } else {
          this.logger.logWarning('ServisBot API has no suitable close method');
          this.isLoading.set(false);
        }
      }
    } catch (error) {
      this.isLoading.set(false);
      this.isVisible.set(false);
      document.body.classList.remove('servisbot-chat-open');
      this.logger.logException(error, 'Failed to toggle ServisBot');
    }
  }

  open(): void {
    this.logger.logTrace('[ServisBotService] open() called - toggling to true');
    this.toggle(true);
  }

  close(): void {
    this.toggle(false);
  }

  async clear(): Promise<void> {
    if (!this.servisBotApi) {
      this.clearDomElements();
      return;
    }

    try {
      await this.servisBotApi.clear();

      this.servisBotApi = null;

      this.selectedLoan = null;
      this.identity = null;
      this.previousLoanCount = 0;
      this.isInitialized.set(false);
      this.isVisible.set(false);
      this.isLoading.set(false);

      localStorage.removeItem('isServisBotVisible');

      this.clearDomElements();

    } catch (error) {
      this.logger.logException(error, 'Failed to clear ServisBot');
      this.servisBotApi = null;
      this.clearDomElements();
    }
  }

  async clearBot(): Promise<void> {
    return this.clear();
  }

  private clearDomElements(): void {
    const messengerIframe = document.getElementById('servisbot-messenger-iframe');
    const roundelIframe = document.getElementById('servisbot-messenger-iframe-roundel');

    if (messengerIframe) {
      messengerIframe.remove();
    }
    if (roundelIframe) {
      roundelIframe.remove();
    }
  }

  setHelocLoanInfo(helocInfo: HelocLoanInfoContext | null): void {
    this.helocLoanInfo = helocInfo;
  }

  private setupVisibilityHandler(): void {
    if (!this.servisBotApi) {
      return;
    }

    try {
      if (typeof this.servisBotApi.on === 'function') {
        this.servisBotApi.on('close', () => {
          this.isVisible.set(false);
          this.isLoading.set(false);
          document.body.classList.remove('servisbot-chat-open');
          document.body.classList.remove('sb-messenger-open');
          document.documentElement.style.removeProperty('overflow');
          document.body.style.removeProperty('overflow');
        });

        this.servisBotApi.on('minimize', () => {
          this.isVisible.set(false);
          this.isLoading.set(false);
          document.body.classList.remove('servisbot-chat-open');
          document.body.classList.remove('sb-messenger-open');
          document.documentElement.style.removeProperty('overflow');
          document.body.style.removeProperty('overflow');
        });

        this.servisBotApi.on('open', () => {
          this.isVisible.set(true);
          this.isLoading.set(false);
          document.body.classList.add('servisbot-chat-open');
        });
      }

      window.addEventListener('message', this.handleIframeMessage.bind(this));

      this.startVisibilityPolling();
    } catch (error) {
      this.logger.logException(error, 'ServisBot visibility handler setup failed');
    }
  }

  private visibilityPollInterval: ReturnType<typeof setInterval> | null = null;

  private startVisibilityPolling(): void {
    if (this.visibilityPollInterval) {
      clearInterval(this.visibilityPollInterval);
    }

    this.visibilityPollInterval = setInterval(() => {
      if (!this.isVisible()) {
        return;
      }

      if (Date.now() - this.lastOpenRequestTime < ServisBotService.OPEN_GRACE_PERIOD_MS) {
        return;
      }

      const iframe = document.getElementById('servisbot-messenger-iframe');
      const iframeParent = document.querySelector('[data-testid="sb-app-iframe-parent"]');
      const servisBotApp = document.querySelector('.servisbot-app');

      const sdkIndicatesClosed =
        iframe?.getAttribute('data-state') === 'minimized' ||
        iframe?.getAttribute('data-state') === 'closed' ||
        iframe?.getAttribute('aria-hidden') === 'true' ||
        iframeParent?.getAttribute('data-state') === 'minimized' ||
        iframeParent?.classList.contains('sb-minimized') ||
        servisBotApp?.getAttribute('data-messenger-open') === 'false' ||
        servisBotApp?.classList.contains('sb-messenger-closed');

      const inlineStyle = iframe?.getAttribute('style') || '';
      const sdkMinimizedViaStyle =
        inlineStyle.includes('height: 0') ||
        inlineStyle.includes('height:0') ||
        inlineStyle.includes('display: none') ||
        inlineStyle.includes('display:none');

      if (sdkIndicatesClosed || sdkMinimizedViaStyle) {
        this.handleChatClosed('visibility poll');
      }
    }, 300);
  }

  private handleIframeMessage(event: MessageEvent): void {
    if (event.data?.source === 'servisbot' ||
        event.data?.type?.includes?.('servisbot') ||
        event.data?.action === 'close' ||
        event.data?.action === 'minimize') {
      if (event.data?.action === 'close' || event.data?.action === 'minimize') {
        this.handleChatClosed('postMessage');
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private handleChatClosed(_source: string): void {
    if (!this.isVisible()) {
      return;
    }
    this.isVisible.set(false);
    this.isLoading.set(false);
    document.body.classList.remove('servisbot-chat-open');
    document.body.classList.remove('sb-messenger-open');
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
  }

  private postMessageToMessenger(action: 'open' | 'close'): boolean {
    const messengerIframe = document.getElementById('servisbot-messenger-iframe') as HTMLIFrameElement | null;
    if (!messengerIframe?.contentWindow) {
      return false;
    }

    try {
      messengerIframe.contentWindow.postMessage({ source: 'sb-host', action }, '*');
      return true;
    } catch (error) {
      this.logger.logException(error, 'ServisBot: postMessage to messenger iframe failed');
      return false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private resetHandler(api: any): void {
    if (this.selectedLoan) {
      this.servisBotApi = api;
      this.setupNotificationHandler();
    }
  }

  private async waitForSdkReady(maxWaitMs = 3000, intervalMs = 100): Promise<void> {
    if (!this.servisBotApi) {
      return;
    }

    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      try {
        if (typeof this.servisBotApi.on === 'function') {
          return;
        }
      } catch {
        // SDK not ready yet, wait and retry
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    this.logger.logWarning('ServisBot SDK ready check timed out, proceeding anyway');
  }

  private setupNotificationHandler(): void {
    if (!this.servisBotApi || !this.isInitialized()) {
      return;
    }

    try {
      this.servisBotApi.on('notification', (res: string) => {
        try {
          const event = JSON.parse(res)['sbNavTo'];
          if (event) {
            const route = this.getRouteForIntent(event);
            this.router.navigate([route]);
          }
        } catch (error) {
          this.logger.logException(
            error instanceof Error ? error : new Error('Failed to handle ServisBot notification'),
          );
        }
      });
    } catch (error) {
      this.logger.logException(error, 'ServisBot notification handler setup deferred - SDK may still be initializing');
    }
  }

  private isDefaultPaymentLoan(): boolean {
    return (
      this.env.appSettings?.AppSettings?.enableDefaultPayment === 'true' && (this.selectedLoan?.loanStatus ?? 0) >= 5
    );
  }

  private isSelectedLoanHeloc(): boolean {
    if (!this.selectedLoan) return false;
    return this.identity
      ? isHelocLoan(this.selectedLoan.loanNumber, this.identity)
      : false;
  }

  private getRouteForIntent(intent: string): string {
    const loanNumber = this.selectedLoan?.loanNumber;

    switch (intent) {
      case ServisBotIntent.SECURE_MESSAGE_CENTER:
        return loanNumber ? `/messages/${loanNumber}` : '/dashboard';
      case ServisBotIntent.PAYMENT:
      case ServisBotIntent.HELOC_PAYMENT:
        if (this.isDefaultPaymentLoan()) {
          return '/default-payment';
        }
        return loanNumber ? `/loans/${loanNumber}/payment` : '/dashboard';
      case ServisBotIntent.PAYOFF_QUOTE:
        if (this.isSelectedLoanHeloc()) {
          return loanNumber ? `/loans/${loanNumber}/details` : '/dashboard';
        }
        return loanNumber ? `/loans/${loanNumber}/payoff-quote` : '/dashboard';
      case ServisBotIntent.PAYMENT_ASSISTANCE:
        return loanNumber ? `/help/${loanNumber}/financial-hardship` : '/dashboard';
      case ServisBotIntent.AUTOPAY:
      case ServisBotIntent.HELOC_AUTOPAY:
        return loanNumber ? `/loans/${loanNumber}/autopay` : '/dashboard';
      case ServisBotIntent.INSURANCE_CLAIMS:
        return loanNumber ? `/help/${loanNumber}/insurance-claims` : '/dashboard';
      case ServisBotIntent.HOMEOWNERS_INSURANCE:
        return loanNumber ? `/help/${loanNumber}/homeowners-insurance` : '/dashboard';
      case ServisBotIntent.PROPERTY_TAXES:
        return loanNumber ? `/help/${loanNumber}/property-taxes` : '/dashboard';
      case ServisBotIntent.FAQS:
        return loanNumber ? `/help/${loanNumber}/faqs` : '/dashboard';
      case ServisBotIntent.STATEMENTS_AND_DOCUMENTS:
        return loanNumber ? `/loans/${loanNumber}/documents` : '/dashboard';
      case ServisBotIntent.USER_PROFILE:
        return loanNumber ? `/contact-information/${loanNumber}` : '/dashboard';
      case ServisBotIntent.REQUEST_FUNDS:
      case ServisBotIntent.HELOC_DRAW:
        return loanNumber ? `/loans/${loanNumber}/request-funds` : '/dashboard';
      case ServisBotIntent.HELOC_DASHBOARD:
        return loanNumber ? `/loans/${loanNumber}/details` : '/dashboard';
      case ServisBotIntent.MANAGE_PAPERLESS:
        return loanNumber ? `/manage-paperless/${loanNumber}` : '/dashboard';
      case ServisBotIntent.DASHBOARD:
        return loanNumber ? `/loans/${loanNumber}/details` : '/dashboard';
      default:
        return '/dashboard';
    }
  }

  private buildLoanList(loans: Loan[]): ServisBotLoanDetails[] {
    const seen = new Set<string>();
    const uniqueLoans = loans.filter((loan) => {
      if (seen.has(loan.loanNumber)) return false;
      seen.add(loan.loanNumber);
      return true;
    });

    return uniqueLoans.map((loan) => ({
      loanNumber: loan.loanNumber,
      encryptionValue: (this.identity ? getEncryptionValueForLoan(this.identity, loan.loanNumber) : null) ?? undefined,
      isOTP: this.isOTPEligible(loan),
      isAutopay: loan.isInhouseAutopayEnabled || loan.isRepayAutopayEnabled,
      isHeloc: this.identity
        ? isHelocLoan(loan.loanNumber, this.identity)
        : loan.productType?.toLowerCase() === 'heloc',
      isBotEligible: this.isBotEligible(loan),
    }));
  }

  private isOTPEligible(loan: Loan): boolean {
    return (
      !loan.isPaidInFull &&
      !loan.isTransferredToCenlar &&
      !loan.isTransferredToThirdParty &&
      loan.foreclosureStopCode !== '1' &&
      !loan.isCh7Discharge &&
      !loan.isCh11_12_13Discharge
    );
  }

  private getLoanBorrowerInfo(loan: Loan, userProfile: UserProfile): Borrower | null {
    if (!userProfile.properties || !loan.borrower) {
      return loan.borrower;
    }

    const loanProperty = userProfile.properties.find(
      (p) => p.key === LoanKeyEnum.LoanNumber && p.value === loan.loanNumber.toString(),
    );

    if (!loanProperty?.attributeCollectionId) {
      return loan.borrower;
    }

    const lastSSNProperty = userProfile.properties.find(
      (p) => p.key === LoanKeyEnum.LastSSN && p.attributeCollectionId === loanProperty.attributeCollectionId,
    );

    const profileSSN = lastSSNProperty?.value;
    if (!profileSSN) {
      return loan.borrower;
    }

    return profileSSN === loan.borrower.ssn?.toString() ? loan.borrower : (loan.coBorrower ?? loan.borrower);
  }

  private isBotEligible(loan: Loan): boolean {
    if (loan.pifStopCode === '1') {
      return true;
    }

    const isValidProcessCode = loan.processStopCode === '0' || loan.processStopCode === 'T';
    if (!isValidProcessCode) {
      return false;
    }

    const validStatuses = [
      PaymentLoanStatusValEnum.OnTime,
      PaymentLoanStatusValEnum.OneMonthPaidOff,
      PaymentLoanStatusValEnum.TwoMonthsPaidOff,
      PaymentLoanStatusValEnum.PastDue,
    ];

    return validStatuses.includes(loan.loanStatus);
  }
}
